import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatPrice } from "@/lib/api";
import { MessageCircle, BadgeCheck, Send, ArrowLeft, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { listingId, peerId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    refresh();
  }, [user, loading, navigate]);

  const refresh = async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
  };

  useEffect(() => {
    if (!listingId || !peerId) {
      setThread([]);
      return;
    }
    (async () => {
      const { data } = await api.get(`/conversations/${listingId}/${peerId}`);
      setThread(data);
      await api.post(`/conversations/${listingId}/${peerId}/read`);
      // refresh sidebar to clear unread badge
      refresh();
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    })();
  }, [listingId, peerId]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/messages/thread?peer_id=${peerId}`, { listing_id: listingId, body });
      setBody("");
      const { data } = await api.get(`/conversations/${listingId}/${peerId}`);
      setThread(data);
      refresh();
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 50);
    } catch {
      toast.error("Erreur d'envoi");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  const activeKey = listingId && peerId ? `${listingId}/${peerId}` : null;
  const activeConv = conversations.find((c) => `${c.listing_id}/${c.peer_id}` === activeKey);

  return (
    <Layout withFooter={false}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Messagerie</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-6">Mes conversations</h1>

        <div className="grid md:grid-cols-[340px_1fr] gap-4 bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden min-h-[70vh]">
          {/* Sidebar */}
          <div className="border-r border-[#E5E5E5] overflow-y-auto max-h-[75vh]" data-testid="conversations-list">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" strokeWidth={1.5} />
                Aucune conversation pour le moment.
              </div>
            ) : (
              conversations.map((c) => {
                const isActive = `${c.listing_id}/${c.peer_id}` === activeKey;
                return (
                  <Link
                    key={`${c.listing_id}/${c.peer_id}`}
                    to={`/messages/${c.listing_id}/${c.peer_id}`}
                    data-testid="conversation-item"
                    className={`flex gap-3 p-4 border-b border-[#F0EBE6] transition-colors ${
                      isActive ? "bg-[#FEF2F3]" : "hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <img
                      src={c.listing?.photos?.[0] || "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=200"}
                      alt=""
                      className="w-12 h-12 rounded-xl object-cover shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm truncate flex items-center gap-1">
                          {c.peer?.name || "Utilisateur"}
                          {c.peer?.badge === "pro_verified" && <BadgeCheck className="w-3 h-3 text-[#11783A]" />}
                        </div>
                        {c.unread_count > 0 && (
                          <span data-testid="unread-badge" className="text-[10px] bg-[#D6202C] text-white px-1.5 py-0.5 rounded-full font-medium">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {c.last_from_me ? "Vous : " : ""}{c.last_message}
                      </div>
                      <div className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">{c.listing?.title}</div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Thread */}
          <div className="flex flex-col">
            {!activeKey ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" strokeWidth={1.5} />
                  Sélectionnez une conversation à gauche
                </div>
              </div>
            ) : (
              <>
                {activeConv?.listing && (
                  <div className="p-4 border-b border-[#E5E5E5] flex items-center gap-3 bg-[#FAFAFA]">
                    <Link to={`/messages`} className="md:hidden">
                      <ArrowLeft className="w-4 h-4" />
                    </Link>
                    <img src={activeConv.listing.photos?.[0]} alt="" className="w-12 h-12 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/listings/${activeConv.listing_id}`} className="font-medium hover:text-[#D6202C] truncate block text-sm">
                        {activeConv.listing.title}
                      </Link>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {activeConv.listing.city} ·{" "}
                        {formatPrice(activeConv.listing.price, activeConv.listing.currency, activeConv.listing.type)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground hidden md:block">
                      Avec <span className="font-medium text-[#1A1A1A]">{activeConv.peer?.name}</span>
                    </div>
                  </div>
                )}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="thread-messages">
                  {thread.map((m) => {
                    const mine = m.from_user_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                            mine
                              ? "bg-[#D6202C] text-white rounded-br-sm"
                              : "bg-[#F5F0EB] text-[#1A1A1A] rounded-bl-sm"
                          }`}
                        >
                          {m.body}
                          <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                            {new Date(m.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-[#E5E5E5] p-3 flex gap-2 bg-white">
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={2}
                    placeholder="Votre réponse..."
                    className="rounded-xl resize-none"
                    data-testid="thread-message-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                  />
                  <Button
                    onClick={send}
                    disabled={busy || !body.trim()}
                    data-testid="thread-send-btn"
                    className="bg-[#D6202C] hover:bg-[#B5181F] rounded-xl self-end h-[60px]"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
